import { type FC, type ReactNode, useEffect, useMemo } from "react";

import {
  LDProvider,
  useFlags,
  useLDClient,
} from "launchdarkly-react-client-sdk";

import { pruneLaunchDarklyCacheIfNearQuota } from "src/contexts/launchdarkly-cache";
import { captureException } from "src/lib/monitoring";
import { useUser } from "./user-context";

export const clientSideID =
  import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID?.toString() ?? "";

export interface LDUserInfo {
  id: number | null;
  name: string | null;
  email: string | null;
}

export type PlanTier = "free" | "paid" | "business";

export interface LDWorkspaceInfo {
  id: number;
  name?: string;
  slug?: string;
  region?: string;
  organizationId?: number;
  planTier?: PlanTier;
  planSku?: string;
}

// Mirrors derivePlanTier in packages/backend/lib/entitlements/plan-tier.ts.
export function derivePlanTier(sku: string | null | undefined): PlanTier {
  if (sku == null) return "free";
  return sku === "business_tier" ? "business" : "paid";
}

export function buildLDContext(
  user: LDUserInfo | null | undefined,
  workspace: LDWorkspaceInfo | null | undefined,
) {
  if (!user) {
    return { kind: "user" as const, anonymous: true };
  }

  return {
    kind: "multi" as const,
    user: {
      key: `user-${user.id?.toString()}`,
      name: user.name,
      email: user.email,
      workspaceId: workspace?.id,
      organizationId: workspace?.organizationId ?? -1,
      planTier: workspace?.planTier,
      planSku: workspace?.planSku,
    },
    ...(workspace?.id
      ? {
          workspace: {
            key: `workspace-${workspace.id}`,
            id: workspace.id,
            name: workspace.name,
            slug: workspace.slug,
            region: workspace.region,
            organizationId: workspace.organizationId,
            planTier: workspace.planTier,
            planSku: workspace.planSku,
          },
        }
      : {}),
  };
}

/**
 * Mirrors the controlPlaneReadReplicaStrategy flag onto window.Hightouch so
 * the non-React GraphQL fetcher can read it per request. useFlags() exposes
 * LaunchDarkly keys camelCased, so the flag is read as
 * controlPlaneReadReplicaStrategy.
 */
const ReadReplicaStrategyBridge: FC = () => {
  const { controlPlaneReadReplicaStrategy } = useFlags();

  useEffect(() => {
    window.Hightouch = {
      ...window.Hightouch,
      readReplicaStrategy:
        typeof controlPlaneReadReplicaStrategy === "string"
          ? controlPlaneReadReplicaStrategy
          : undefined,
    };
  }, [controlPlaneReadReplicaStrategy]);

  return null;
};

// Every workspace and plan is its own flag context, so old snapshots pile up until storage nears the quota.
const LaunchDarklyCachePruner: FC = () => {
  const ldClient = useLDClient();

  useEffect(() => {
    if (!ldClient) {
      return;
    }
    let cancelled = false;
    const prune = async () => {
      try {
        await ldClient.waitUntilReady();
        if (!cancelled) {
          pruneLaunchDarklyCacheIfNearQuota(
            clientSideID,
            ldClient.getContext(),
          );
        }
      } catch (err) {
        console.warn("Failed to prune LaunchDarkly flag snapshots", err);
      }
    };
    void prune();
    return () => {
      cancelled = true;
    };
  }, [ldClient]);

  return null;
};

interface FeatureFlagProviderProps {
  children: ReactNode;
}

export const FeatureFlagProvider: FC<FeatureFlagProviderProps> = ({
  children,
}) => {
  const { user, workspace } = useUser();

  // Guard on `!user` rather than `isLoading` intentionally. `user` is undefined
  // in two cases: (1) during loading, and (2) after an auth error. Using
  // `isLoading` would pass the anonymous fallback context to LD on auth errors
  // (isLoading=false, user=undefined), which is wrong. With `!user`, LD stays
  // deferred until we have a real user in both cases.
  //
  // Once user data arrives, the context transitions from undefined -> real
  // context, which triggers the SDK to initialize via deferInitialization.
  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  const context = useMemo(() => {
    if (!user) return undefined;
    return buildLDContext(
      user,
      workspace
        ? {
            id: workspace.id,
            name: workspace.name,
            slug: workspace.slug ?? undefined,
            region: workspace.region,
            organizationId: workspace.organization?.id,
            planTier: derivePlanTier(workspace.organization?.plan?.sku),
            planSku: workspace.organization?.plan?.sku ?? undefined,
          }
        : null,
    );
  }, [
    user?.name,
    user?.email,
    user?.id,
    workspace?.id,
    workspace?.name,
    workspace?.slug,
    workspace?.region,
    workspace?.organization?.id,
    workspace?.organization?.plan?.sku,
  ]);

  return (
    <LDProvider
      clientSideID={clientSideID}
      // Seconds. Routes wait for the client, so a hung flag request must not block them forever.
      timeout={5}
      deferInitialization
      context={context}
      options={{
        bootstrap: "localStorage",
      }}
    >
      <ReadReplicaStrategyBridge />
      <LaunchDarklyCachePruner />
      <FeatureFlagLoader>{children}</FeatureFlagLoader>
    </LDProvider>
  );
};

interface FeatureFlagLoaderProps {
  children: ReactNode;
}

export const FeatureFlagLoader: FC<FeatureFlagLoaderProps> = ({ children }) => {
  const launchDarkly = useLDClient();

  useEffect(() => {
    if (launchDarkly) {
      launchDarkly.waitForInitialization(5).catch((error) => {
        captureException(error, {
          tags: { component: "FeatureFlagLoader" },
        });
      });
    }
  }, [launchDarkly]);

  // Always render children to avoid unmounting the subtree during LD
  // initialization, which would cancel in-flight react-query prefetches.
  return <>{children}</>;
};
